import {
  Eye,
  AlertTriangle,
  HelpCircle,
  Sparkles,
  Lightbulb,
  Flame,
  Compass,
  Target,
  MessageCircleQuestion,
  Quote,
  TrendingUp,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type { ToolAccent } from "./types";

/**
 * Allowlist of icons facilitators can pick for an insight tool.
 * Keep the list small — every entry adds a swatch to the icon picker.
 */
export const TOOL_ICONS: Record<string, LucideIcon> = {
  Eye,
  AlertTriangle,
  HelpCircle,
  Sparkles,
  Lightbulb,
  Flame,
  Compass,
  Target,
  MessageCircleQuestion,
  Quote,
  TrendingUp,
  Zap,
};

export const TOOL_ICON_NAMES = Object.keys(TOOL_ICONS);

export const TOOL_ACCENTS: ToolAccent[] = ["berry", "lime", "paper", "mist"];

/**
 * Tailwind class fragments per accent. Used by InsightCard and the ToolsEditor.
 * The "stage" variant is for the dark stage background; "panel" for the cream
 * facilitator surface; "swatch" is the solid pill used in the picker / row badge.
 */
export const ACCENT_CLASSES: Record<
  ToolAccent,
  { stageBorder: string; stageBg: string; stageIconBg: string; stageIconText: string; stageLabelText: string; swatch: string }
> = {
  berry: {
    stageBorder: "border-rc-berry/40",
    stageBg: "bg-rc-berry/12",
    stageIconBg: "bg-rc-berry",
    stageIconText: "text-rc-paper",
    stageLabelText: "text-rc-berry",
    swatch: "bg-rc-berry text-rc-paper",
  },
  lime: {
    stageBorder: "border-rc-lime/45",
    stageBg: "bg-rc-lime/15",
    stageIconBg: "bg-rc-lime",
    stageIconText: "text-rc-black",
    stageLabelText: "text-rc-lime",
    swatch: "bg-rc-lime text-rc-black",
  },
  paper: {
    stageBorder: "border-rc-paper/20",
    stageBg: "bg-rc-paper/8",
    stageIconBg: "bg-rc-paper/15",
    stageIconText: "text-rc-paper",
    stageLabelText: "text-rc-paper",
    swatch: "bg-rc-paper text-rc-black border border-rc-fog",
  },
  mist: {
    stageBorder: "border-rc-mist/40",
    stageBg: "bg-rc-mist/20",
    stageIconBg: "bg-rc-mist",
    stageIconText: "text-rc-charcoal",
    stageLabelText: "text-rc-mist",
    swatch: "bg-rc-mist text-rc-charcoal",
  },
};

export interface ToolDefault {
  tool_key: string;
  label: string;
  icon: string;
  accent: ToolAccent;
  prompt: string;
  cadence_first: number | null;
  cadence_step: number | null;
  sort_order: number;
}

const OUTLIER_PROMPT = `AUFGABE: Identifiziere EINEN Outlier — eine Antwort, die deutlich abweicht, eine dominante Annahme herausfordert oder aus einer klar anderen Perspektive kommt.

HARTE EINSCHRÄNKUNGEN:
- Der Outlier muss eine tatsächliche Antwort aus der oben aufgeführten Liste sein. Erfinde keine, beschreibe keine hypothetische "fehlende" Antwort.
- Ein Outlier weicht von der Mehrheit ab UND beantwortet die gestellte Frage. Etikettiere keine Antwort als Outlier, nur weil sie die Frage umformuliert oder ihr ausweicht.
- Benenne konkret, was die Antwort von den dominanten Clustern unterscheidet (anderer Mechanismus, andere Reichweite, andere Annahme).
- Wenn keine Antwort wirklich heraussticht, sage das klar, statt eine zu erzwingen.

OUTPUT-FORMAT (News-Ticker):
- Genau EIN kurzer Satz, maximal 140 Zeichen.
- Kein Vorwort wie "Ein Outlier zeigt...", "Eine Antwort hebt sich ab...". Das Label OUTLIER steht im Ticker bereits davor.
- Aktive Verben. Keine Hedge-Wörter ("vielleicht", "möglicherweise", "scheint").
- Inhaltlich konkret, nicht abstrakt. Paraphrasieren statt zitieren.

ANTWORT: Nur der eine Satz selbst — kein JSON, keine Anführungszeichen, keine Erklärung davor oder danach.
Sei direkt. Kein Hedging. Das läuft als Schlagzeile über die Bühne.`;

const BLIND_SPOT_PROMPT = `AUFGABE: Benenne eine Perspektive, Sorge oder einen Blickwinkel, den die Teilnehmer:innen NICHT genannt haben, der aber im Antwort-Spektrum der gestellten Frage liegt. Der blinde Fleck muss eine Antwort sein, die der Raum vernünftigerweise hätte geben können, aber nicht gegeben hat. Eine fehlende Art von Antwort, keine Meta-Kritik daran, dass die Frage überhaupt beantwortet wurde.

HARTE EINSCHRÄNKUNGEN, deren Verletzung deine Antwort ungültig macht:
- Kritisiere die Teilnehmer:innen NICHT dafür, dass sie die gestellte Frage beantwortet haben. Wenn die Frage konkret nach X fragt, ist "ihr seid alle direkt auf X gesprungen" ungültig, denn das ist genau das, was die Frage erfragt.
- Schwenke NICHT zu Sekundärfolgen, Nachteilen oder Risiken des Frage-Themas, außer du formulierst sie als fehlende ANTWORT-Kategorie innerhalb des Frage-Spektrums.
- Schlage NICHT vor, die Teilnehmer:innen hätten die Prämisse der Frage hinterfragen sollen. Die Prämisse ist gegeben.
- Der blinde Fleck muss eine konkrete Kategorie sein (eine Tätigkeit, eine Fähigkeit, ein Kontext, eine Stakeholder-Perspektive), die in den Antwort-Raum der Frage gehört, aber in Clustern und Antworten fehlt.
- Bevor du schreibst, prüfe still: "Könnte dieser blinde Fleck selbst als gültige Antwort auf die ursprüngliche Frage formuliert werden?" Wenn nein, fange neu an.

OUTPUT-FORMAT (News-Ticker):
- Genau EIN kurzer Satz, maximal 140 Zeichen.
- Kein Vorwort wie "Ein blinder Fleck ist...", "Was fehlt, ist...". Das Label BLINDER FLECK steht im Ticker bereits davor.
- Benenne direkt die fehlende Kategorie (Akteur, Tätigkeit, Kontext, Perspektive).
- Aktive Verben. Keine Hedge-Wörter. Keine Bullet Points.

ANTWORT: Nur der eine Satz selbst, spezifisch für DIESEN Kreis — kein JSON, keine Anführungszeichen, keine Erklärung davor oder danach.
Das läuft als Schlagzeile über die Bühne.`;

const CRITICAL_QUESTION_PROMPT = `AUFGABE: Generiere eine scharfe, provozierende Folgefrage, die den impliziten Konsens im TOP-CLUSTER herausfordert. Sie soll den Raum produktiv unbequem machen: nicht gegensätzlich um des Widerspruchs willen, sondern eine echte Probe einer Annahme, die die Teilnehmer:innen machen.

HARTE EINSCHRÄNKUNGEN:
- Die Frage muss eine Annahme INNERHALB des Top-Cluster-Konsenses prüfen (was er impliziert, was daraus folgt, wovon er abhängt), nicht die Teilnehmer:innen dafür angreifen, dass sie die ursprüngliche Frage beantwortet haben.
- Schimpfe nicht. Beginne nicht mit "Ihr alle...". Die Provokation kommt aus der Schärfe der Probe, nicht aus Schuldzuweisung.
- Die Frage muss beantwortbar sein: sie öffnet einen nächsten Diskussionszug, keine rhetorische Sackgasse.
- Verankere sie in Spezifika aus den Beispiel-Einträgen des Top-Clusters, damit der Raum seine eigene Sprache wiedererkennt.

OUTPUT-FORMAT (News-Ticker):
- Genau EINE kurze Frage, maximal 140 Zeichen.
- Kein Vorwort wie "Eine kritische Frage wäre...". Das Label KRITISCHE FRAGE steht im Ticker bereits davor.
- Starte direkt mit dem Fragewort (Wer, Was, Wie, Wann, Warum, Wenn …).
- Keine Doppelfrage, keine rhetorische Frage. Eine echte Probe.

ANTWORT: Nur die eine Frage selbst, spezifisch für das, was DIESER Kreis gesagt hat — kein JSON, keine Anführungszeichen, keine Erklärung davor oder danach.
Das läuft als Schlagzeile über die Bühne.`;

/**
 * Mirrors the SQL `seed_event_tools_for()` function. Used by "Reset to default"
 * in the ToolsEditor — keep the prompt strings byte-for-byte identical so a
 * reset truly restores the seed.
 */
export const TOOL_DEFAULTS: ToolDefault[] = [
  {
    tool_key: "outlier",
    label: "Outlier",
    icon: "Eye",
    accent: "berry",
    prompt: OUTLIER_PROMPT,
    cadence_first: 10,
    cadence_step: 20,
    sort_order: 0,
  },
  {
    tool_key: "blind_spot",
    label: "Blinder Fleck",
    icon: "AlertTriangle",
    accent: "lime",
    prompt: BLIND_SPOT_PROMPT,
    cadence_first: 15,
    cadence_step: 30,
    sort_order: 1,
  },
  {
    tool_key: "critical_question",
    label: "Kritische Frage",
    icon: "HelpCircle",
    accent: "paper",
    prompt: CRITICAL_QUESTION_PROMPT,
    cadence_first: null,
    cadence_step: null,
    sort_order: 2,
  },
];

export function getDefaultForKey(toolKey: string): ToolDefault | null {
  return TOOL_DEFAULTS.find((d) => d.tool_key === toolKey) ?? null;
}
